/**
 * The update channels: their words, their version ordering, and the rules a
 * channel switch has to obey.
 *
 * Everything here is pure. The feed URLs, the stored file and Velopack itself
 * live in `apps/desktop/src/main/update-channel.ts` and `.../updates.ts`; this
 * module is what BOTH processes have to agree on, because the same sentences are
 * composed in the main process for the log and the support transcript and in the
 * renderer for the setting and the update banner. A second copy of these words
 * in the renderer is how the banner came to name the wrong channel.
 *
 * `tests/update-channel-tests/run.mjs` exercises this file directly and prints
 * every composed sentence, which is the only way to read them without launching
 * the application.
 */

import type { UpdateChannel, UpdateChannelChoice, UpdateStatus } from './desktop-api';

/** Public names invite early access; persisted channel IDs and feed paths stay unchanged. */
export const updateChannelChoices: UpdateChannelChoice[] = [
  {
    channel: 'stable',
    title: 'Regular',
    detail: 'Receive general releases for everyday work.',
  },
  {
    channel: 'beta',
    title: 'Early access',
    detail: 'Try upcoming releases before their general release. Features are still being refined.',
  },
];

/** What a channel is called on screen. Never the stored value. */
export const channelTitle = (channel: UpdateChannel): string =>
  updateChannelChoices.find((choice) => choice.channel === channel)?.title ?? channel;

/**
 * Whether a value really is one of the two channels.
 *
 * Used at the IPC boundary and when reading the stored file, per the desktop
 * security policy: this value ends up in a feed URL, so nothing that was not
 * checked may reach it.
 */
export const isUpdateChannel = (value: unknown): value is UpdateChannel =>
  value === 'stable' || value === 'beta';

/**
 * Stable, for a workstation that has never said otherwise.
 *
 * The default is the cautious one because the cost of the two mistakes is not
 * symmetric: a machine that stays on a tested build for longer than it needed to
 * has lost some improvements, while a machine carried onto an untested build in
 * the middle of a working day can cost an operator their afternoon.
 */
export const defaultUpdateChannel: UpdateChannel = 'stable';

/** A package and the channel it was fetched from. */
export type DownloadedFrom = { version: string; channel: UpdateChannel };

/**
 * Which channel produced each package in Velopack's packages directory, by
 * version.
 *
 * This is the record that stops a channel switch from lying across a restart.
 * Velopack can report a package as already downloaded and ready to apply, but it
 * cannot say which feed produced it, and the answer stops being guessable the
 * moment the setting changes. Without this, an operator who downloaded 6.1.63 on
 * Preview, moved to Stable, and reopened the product would be offered that same
 * Preview build again with "Stable" written next to it.
 *
 * A MAP rather than the single slot this started as. The packages directory
 * holds more than one package, and a single slot was destroyed by the next
 * download: switch to Preview, start a large Preview download, switch back to Stable
 * mid-download, and the Stable check's own record overwrote the Preview one — after
 * which the Preview package on disk was unattributable and the guards that depended
 * on knowing its origin all switched off.
 *
 * Each entry is written BEFORE its download starts rather than after it
 * finishes, because the interesting case is a download that was still running
 * when the channel changed: those bytes land on disk whether or not anybody is
 * still waiting for them, and they have to be attributable when they do.
 */
export type DownloadedOrigins = Record<string, UpdateChannel>;

/** Which channel a downloaded package came from, or null when nothing knows. */
export const originOfDownload = (
  origins: DownloadedOrigins,
  version: string,
): UpdateChannel | null => (isUpdateChannel(origins[version]) ? origins[version] : null);

/**
 * Record where a package came from, dropping entries that can no longer matter.
 *
 * Anything at or below the installed version is pruned: `releaseIsOfferable`
 * refuses those outright, so their origin is never consulted again and keeping
 * them would grow this file for ever. The cap underneath that is paranoia — an
 * evicted entry becomes an unknown origin, which is refused rather than
 * installed, so pruning too eagerly is the safe direction to be wrong in.
 */
export const rememberDownload = (
  origins: DownloadedOrigins,
  version: string,
  channel: UpdateChannel,
  installedVersion: string,
): DownloadedOrigins => {
  const kept: DownloadedOrigins = { [version]: channel };
  const survivors = Object.keys(origins)
    .filter((known) => known !== version && releaseIsOfferable(installedVersion, known))
    .sort((left, right) => compareReleaseVersions(right, left) ?? 0)
    .slice(0, 15);
  for (const known of survivors) {
    const origin = originOfDownload(origins, known);
    if (origin) kept[known] = origin;
  }
  return kept;
};

/** Forget one package, because it has just been handed to the updater. */
export const forgetDownload = (origins: DownloadedOrigins, version: string): DownloadedOrigins => {
  const kept: DownloadedOrigins = {};
  for (const known of Object.keys(origins)) {
    const origin = originOfDownload(origins, known);
    if (known !== version && origin) kept[known] = origin;
  }
  return kept;
};

// ---------------------------------------------------------------------------
// Version ordering
// ---------------------------------------------------------------------------

/**
 * The version scheme this product publishes, stated so the comparator below can
 * be held to something: `MAJOR.MINOR.PATCH`, an optional fourth numeric segment,
 * then an optional `-prerelease` and `+build`. That is semantic-versioning
 * precedence widened by the fourth segment NuGet and Velopack accept, and
 * `tools/release/Pack-Release.ps1` now REFUSES to pack a version outside it —
 * the rule and its enforcement have to travel together or the rule is a wish.
 *
 * The constraint matters because this comparator GATES BEHAVIOUR rather than
 * only choosing words: it decides whether a release is offered at all. Two
 * failures on record. The implementation this replaces split on `[.+-]` and
 * mapped every non-numeric segment to zero, which made `6.1.58-beta.2` compare
 * EQUAL to `6.1.58`. And the version pattern was three segments while nothing
 * upstream stopped a four-part tag, so `6.1.65.0` against `6.1.70` was
 * unorderable — and, on the old rule that unorderable versions were offered, it
 * would have been installed as a downgrade.
 */
const versionPattern =
  /^v?(\d+)\.(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/;

type ParsedVersion = { release: [number, number, number, number]; prerelease: (string | number)[] };

const parseVersion = (value: string): ParsedVersion | null => {
  const match = versionPattern.exec(value.trim());
  if (!match) return null;
  const prerelease = match[5]
    ? match[5].split('.').map((piece) => (/^\d+$/.test(piece) ? Number.parseInt(piece, 10) : piece))
    : [];
  // An empty identifier ("6.1.58-beta..2") is not a version we published.
  if (prerelease.some((piece) => piece === '')) return null;
  return {
    release: [
      Number.parseInt(match[1], 10),
      Number.parseInt(match[2], 10),
      match[3] ? Number.parseInt(match[3], 10) : 0,
      match[4] ? Number.parseInt(match[4], 10) : 0,
    ],
    prerelease,
  };
};

/**
 * Semantic-versioning precedence: -1, 0 or 1, or **null when the two cannot be
 * ordered**, because either side is not a version this product publishes.
 *
 * Null rather than a guess. Every caller either refuses an update or makes a
 * claim to an operator, and both are worse done confidently on a string nobody
 * understood. Build metadata is ignored, as the specification requires; a
 * prerelease sorts BELOW the release that shares its numbers.
 */
export const compareReleaseVersions = (left: string, right: string): number | null => {
  const a = parseVersion(left);
  const b = parseVersion(right);
  if (!a || !b) return null;

  for (let index = 0; index < 4; index++)
    if (a.release[index] !== b.release[index]) return a.release[index] > b.release[index] ? 1 : -1;

  // 6.1.58 outranks 6.1.58-beta.2: the finished release is later than any
  // prerelease of the same numbers.
  if (a.prerelease.length === 0 && b.prerelease.length === 0) return 0;
  if (a.prerelease.length === 0) return 1;
  if (b.prerelease.length === 0) return -1;

  for (let index = 0; index < Math.max(a.prerelease.length, b.prerelease.length); index++) {
    const one = a.prerelease[index];
    const two = b.prerelease[index];
    // A shorter run of identifiers sorts first: 6.1.58-beta < 6.1.58-beta.2.
    if (one === undefined) return -1;
    if (two === undefined) return 1;
    if (one === two) continue;
    const oneIsNumber = typeof one === 'number';
    const twoIsNumber = typeof two === 'number';
    if (oneIsNumber && twoIsNumber) return one > two ? 1 : -1;
    // A numeric identifier always sorts below an alphanumeric one.
    if (oneIsNumber) return -1;
    if (twoIsNumber) return 1;
    return String(one) > String(two) ? 1 : -1;
  }
  return 0;
};

/**
 * Whether a release may be offered to a workstation already on
 * `installedVersion` — the rule that makes the "ahead of your channel" sentence
 * true rather than merely reassuring.
 *
 * An installed build cannot be un-applied. Offering a lower version is offering
 * something that cannot be honoured, and an operator who accepts it has been
 * told a lie about what their workstation is about to do. So a release is
 * offerable only when it is **provably** newer.
 *
 * Two versions that cannot be ordered are REFUSED. An earlier draft offered them
 * — reasoning that refusing on a string nobody parsed would strand a workstation
 * — and that was the wrong way round: an unorderable pair is exactly a pair that
 * might be a downgrade, so "offer it" is an unrecognised case falling through to
 * the permissive branch. Stranding is visible, reversible by a reinstall, and
 * cannot happen anyway now that `Pack-Release.ps1` refuses to publish a version
 * this comparator cannot read. A silent downgrade over a working installation is
 * none of those things.
 */
export const releaseIsOfferable = (installedVersion: string, candidateVersion: string): boolean =>
  compareReleaseVersions(candidateVersion, installedVersion) === 1;

/*
 * There is deliberately no `downloadedPackageIsUsable` here any more.
 *
 * It read `!downloaded || downloaded.version !== pendingVersion || downloaded.channel === channel`,
 * so a record naming a DIFFERENT version waved the package through as usable —
 * and once through, its origin came back null, which both remaining guards
 * treated as "no objection" rather than as "no idea". Unknown provenance
 * defaulting to allowed is the same shape of defect as an unrecognised finding
 * falling through to the permissive branch.
 *
 * `updates.ts` now asks `originOfDownload` for the answer and has exactly three
 * branches for it: this channel, another channel, or unknown — and unknown is
 * never staged on trust. It is either proved against the feed or refused.
 */

// ---------------------------------------------------------------------------
// What the product says
// ---------------------------------------------------------------------------

/**
 * What to tell an operator who moves to a channel whose newest release is behind
 * the version they are already running, or null when there is nothing to say.
 *
 * This is the case that will actually happen, because Preview usually leads: a
 * workstation on Preview at 6.1.58 switching to Stable when the newest stable
 * release is 6.1.50. It must NOT be rolled backwards, and `releaseIsOfferable`
 * above is what enforces that — this sentence only reports it. The workstation
 * stays where it is and simply stops moving until a stable release passes it,
 * and saying so is how it avoids appearing to do nothing.
 */
export const aheadOfChannelNotice = (
  installedVersion: string,
  newestOnChannel: string | null,
  channel: UpdateChannel,
): string | null => {
  if (!newestOnChannel) return null;
  const order = compareReleaseVersions(installedVersion, newestOnChannel);
  // Nothing is claimed about two versions that could not be ordered.
  if (order === null || order <= 0) return null;
  const title = channelTitle(channel);
  return (
    `This computer is on ${installedVersion}, which is newer than the current ${title} release ` +
    `(${newestOnChannel}). It will stay on ${installedVersion} and update again when the next ${title} release goes past it.`
  );
};

/**
 * What happened to work already in flight when the channel changed.
 *
 * The setting is not a preference that takes effect later; it lands on a running
 * updater. Three things can be underway — nothing, a check, a downloaded package
 * — and each has to be accounted for out loud. A switch that silently did
 * nothing, or that kept a Preview package and relabelled it as Stable, is the
 * failure this sentence exists to make impossible to ship.
 */
export const channelSwitchNotice = (
  from: UpdateChannel,
  to: UpdateChannel,
  discarded: DownloadedFrom | null,
  checkWasRunning: boolean,
): string => {
  // This receipt is retained after the check ends, including in development
  // where no installed updater exists. Live activity belongs to UpdateStatus.
  const next =
    to === 'beta' ? 'Early access is on.' : 'Early access is off. You’ll receive regular releases.';
  if (discarded) {
    const stopped = checkWasRunning ? ', and the check that was running was stopped' : '';
    return (
      `Paige ${discarded.version} had already been downloaded from ${channelTitle(discarded.channel)}. ` +
      `It has been discarded and will not be installed${stopped}. ${next}`
    );
  }
  if (checkWasRunning)
    return `The check that was running was looking at ${channelTitle(from)}, so it was stopped. ${next}`;
  return next;
};

/**
 * One sentence for what the updater is doing, or for what it last found.
 *
 * Every state carries its own words, including the ones that were previously a
 * blank surface: "checking" says what it is waiting for, and "ahead" says why
 * nothing is going to arrive. A package always names the channel it came from —
 * never the channel the workstation is on now, which is the thing that would be
 * wrong precisely when it mattered. There is no unlabelled case, because a
 * package whose origin is unknown is never staged in the first place.
 */
export const updateActivityLine = (status: UpdateStatus): string => {
  switch (status.state) {
    case 'disabled':
      return status.reason;
    case 'idle':
      return 'No update is waiting. This computer is up to date.';
    case 'checking':
      return 'Looking for a newer release now.';
    case 'downloading':
      return `Downloading ${status.version} from ${channelTitle(status.channel)}: ${Math.round(status.percent)}%.`;
    case 'ready':
      return `${status.version} has been downloaded from ${channelTitle(status.channel)} and can install when Paige is idle and closes.`;
    case 'ahead':
      return status.notice;
    case 'failed':
      return `The last check did not finish: ${status.reason}`;
  }
};

/**
 * The updater's line in the copyable support transcript.
 *
 * A support representative reading "the customer is on 6.1.63 and nothing is
 * arriving" needs three facts in one line: which channel, which build, and what
 * the updater last did about it. The channel a waiting package came from is part
 * of that, because "downloaded from Preview while the setting says Stable" explains
 * a call that is otherwise unexplainable.
 */
export const updateSupportLine = (
  channel: UpdateChannel,
  installedVersion: string,
  status: UpdateStatus,
): string =>
  `${channelTitle(channel)} channel. Installed ${installedVersion}. ${updateActivityLine(status)}`;
